// Firebase v9+ modular SDK
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import {
  arrayUnion,
  serverTimestamp,
  increment,
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  onSnapshot
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyAxXVyeVFlxumGcAe8v9bDyy977ytFJTIg",
  authDomain: "spot-feedback-carousel.firebaseapp.com",
  projectId: "spot-feedback-carousel",
  storageBucket: "spot-feedback-carousel.appspot.com",
  messagingSenderId: "559838262486",
  appId: "1:559838262486:web:4521526930b2aa99b3c577",
  measurementId: "G-3WYBEEWDP7"
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase services
const firestoreInstance = getFirestore(firebaseApp);
const authInstance = getAuth(firebaseApp);

// Create a compatibility layer for auth that provides v8-style API
const auth = {
  ...authInstance,
  currentUser: authInstance.currentUser
};

// Simple v8-style compatibility layer without modifying Firebase objects
const firestore = {
  collection: (collectionPath) => {
    const colRef = collection(firestoreInstance, collectionPath);

    // Return a clean wrapper that doesn't modify Firebase objects
    return {
      // For react-firebase-hooks - return the actual Firebase collection reference
      _delegate: colRef,

      // Expose Firebase v9 properties that react-firebase-hooks might need
      ...colRef,

      doc: (docId) => {
        if (docId) {
          const docRef = doc(firestoreInstance, collectionPath, docId);
          return {
            get: () => getDoc(docRef),
            set: (data, options) => setDoc(docRef, data, options || {}),
            update: (data) => updateDoc(docRef, data),
            delete: () => deleteDoc(docRef)
          };
        }
        return {
          set: (data, options) => {
            const newDocRef = doc(firestoreInstance, collectionPath);
            return setDoc(newDocRef, data, options || {});
          }
        };
      },

      add: (data) => addDoc(colRef, data),

      where: (field, operator, value) => {
        const queryRef = query(colRef, where(field, operator, value));

        // Return a query object that supports chaining and react-firebase-hooks
        return {
          ...queryRef,
          limit: (limitCount) => {
            return query(colRef, where(field, operator, value), limit(limitCount));
          },
          orderBy: (fieldName, direction) => {
            return query(colRef, where(field, operator, value), orderBy(fieldName, direction));
          },
          get: () => getDocs(queryRef),
          onSnapshot: (callback, errorCallback) => onSnapshot(queryRef, callback, errorCallback)
        };
      },

      limit: (limitCount) => {
        const queryRef = query(colRef, limit(limitCount));
        return {
          ...queryRef,
          where: (field, operator, value) => {
            return query(colRef, limit(limitCount), where(field, operator, value));
          },
          orderBy: (fieldName, direction) => {
            return query(colRef, limit(limitCount), orderBy(fieldName, direction));
          },
          get: () => getDocs(queryRef),
          onSnapshot: (callback, errorCallback) => onSnapshot(queryRef, callback, errorCallback)
        };
      },

      orderBy: (field, direction) => {
        return query(colRef, orderBy(field, direction));
      },

      get: () => getDocs(colRef),
      onSnapshot: (callback, errorCallback) => onSnapshot(colRef, callback, errorCallback)
    };
  }
};

// Export Firebase v9+ functions and services
export {
  firestore,
  auth,
  GoogleAuthProvider,
  arrayUnion,
  serverTimestamp,
  increment,
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  limit,
  orderBy,
  onSnapshot
};

// Also export the raw Firebase v9+ instances for react-firebase-hooks compatibility
export {
  firestoreInstance as firestoreV9,
  authInstance as authV9
};

// Legacy firebase namespace for compatibility with existing code
const firebase = {
  firestore: {
    FieldValue: {
      arrayUnion,
      serverTimestamp,
      increment
    }
  }
};

export default firebase;